/**
 * Portable, type-preserving codec for relation-cache values (ADR-011).
 *
 * Every cached 1:1 relation snapshot is run through `@nhtio/encoder` before it is
 * handed to the cache service, and decoded again on read. The encoder round-trips
 * the rich types Lucid columns produce post-`consume` (Luxon `DateTime`, `bigint`,
 * `Date`, `Map`/`Set`, typed arrays, …), so cache-value fidelity no longer depends
 * on the host's BentoCache serializer preserving those types — the cache only ever
 * sees an opaque encoded string.
 *
 * @module
 */

import { decode, encode } from '@nhtio/encoder'
import type { Encodable } from '@nhtio/encoder'

/**
 * Encodes a consumed-`$attributes` snapshot into a portable string for the cache.
 *
 * @param snapshot - The property-keyed, post-`consume` attribute snapshot.
 * @returns The encoded cache value.
 */
export const encodeCacheValue = (snapshot: Record<string, unknown>): string =>
  encode(snapshot as Encodable)

/**
 * Decodes a cache value produced by {@link encodeCacheValue} back into a snapshot.
 *
 * @param encoded - The encoded cache value read from the cache service.
 * @returns The decoded `$attributes` snapshot.
 */
export const decodeCacheValue = (encoded: string): Record<string, unknown> =>
  decode<Encodable>(encoded) as Record<string, unknown>
